import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import {
  IconStethoscope,
  IconUser,
  IconCircleCheckFilled,
  IconSearch,
  IconX,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { Checkbox, LoadingOverlay } from '@mantine/core'
import { normalizeErrorString } from '@medplum/core'
import type { Patient } from '@medplum/fhirtypes'
import { useMedplumProfile } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { useSessionState } from '@/hooks/useSessionState'
import { logError } from '@/utils/errorUtils'
import { formatProviderName } from '@/utils/providerUtils'
import { formatPatientName } from '@/utils.ts'
import { PatientSelect } from '@/pages/CallCenterHome/components/PatientSelect/PatientSelect'
import { buildPatientChartPath } from '@/pages/Patient/patientChartPath'
import {
  closeEncounter,
  ENCOUNTER_CLOSED_BY_TAG_SYSTEM,
  fetchCareCoordinationEncounters,
  isEncounterClosedBy,
  uncloseEncounter,
  type CareCoordinationEncounterItem,
} from '../service'
import {
  CollapsibleSectionCard,
  DATE_FORMAT,
  DateRangeRow,
  FilterPills,
  presetIdToDateFrom,
  resolvePresetRange,
} from './shared'

const ENCOUNTER_FILTERS = [
  { id: 'today', label: 'Today' },
  { id: '7days', label: '7 Days' },
  { id: '30days', label: '30 Days' },
] as const

type EncounterFilterId = (typeof ENCOUNTER_FILTERS)[number]['id']
type UndocumentedFilterId = EncounterFilterId
type SubmittedFilterId = EncounterFilterId

const RED_THRESHOLD_HOURS = 48
const FETCH_PAGE_SIZE = 200

function formatTimeAgo(iso: string): string {
  const now = dayjs()
  const isFuture = dayjs(iso).isAfter(now)
  const diffHr = now.diff(iso, 'hour')
  const diffDays = now.diff(iso, 'day')
  const absHr = Math.abs(diffHr)
  const absDays = Math.abs(diffDays)
  const suffix = (unitCount: number, singular: string, plural: string) =>
    `${unitCount} ${unitCount === 1 ? singular : plural}`

  switch (true) {
    case absHr < 1: {
      const diffMin = Math.abs(now.diff(iso, 'minute'))
      return isFuture ? `in ${diffMin} min` : `${diffMin} min ago`
    }
    case absDays < 1:
      return isFuture
        ? `in ${suffix(absHr, 'hour', 'hours')}`
        : `${suffix(absHr, 'hour', 'hours')} ago`
    default:
      return isFuture
        ? `in ${suffix(absDays, 'day', 'days')}`
        : `${suffix(absDays, 'day', 'days')} ago`
  }
}

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes <= 0) return ''
  if (minutes < 60) return `${minutes} min`
  const hrs = Math.floor(minutes / 60)
  const remainderMin = minutes - hrs * 60
  if (remainderMin === 0) return `${hrs} hr`
  return `${hrs} hr ${remainderMin} min`
}

function formatStartLine(iso: string, durationMinutes: number | null): string {
  const duration = formatDuration(durationMinutes)
  const datePart = dayjs(iso).format('MMM D, YYYY, h:mm A')
  return duration ? `${datePart} · ${duration}` : datePart
}

function formatSubmittedTime(iso: string): string {
  const day = dayjs(iso)
  const today = dayjs().startOf('day')
  const prefix = day.isSame(today, 'day') ? 'Today' : day.format('MMM D, YYYY')
  return `${prefix}, ${day.format('h:mm A')}`
}

function isOverdue(iso: string): boolean {
  return dayjs().diff(iso, 'hour') >= RED_THRESHOLD_HOURS
}

function getDurationMinutes(item: CareCoordinationEncounterItem): number | null {
  const start = item.resource.period?.start
  const end = item.resource.period?.end
  if (!start || !end) return null
  const diff = dayjs(end).diff(start, 'minute')
  return diff > 0 ? diff : null
}

function getEncounterVisitType(item: CareCoordinationEncounterItem): string {
  const type = item.resource.type?.[0]
  if (type?.text) return type.text
  const coding = type?.coding?.[0]?.display
  if (coding) return coding
  const reasonText = item.resource.reasonCode?.[0]?.text
  if (reasonText) return reasonText
  return item.appointment?.description ?? ''
}

function getPatientLabel(item: CareCoordinationEncounterItem): string {
  const formatted = formatPatientName(item.patient?.name?.[0])
  if (formatted) return formatted
  return item.resource.subject?.display ?? 'Unknown patient'
}

function getPractitionerLabel(item: CareCoordinationEncounterItem): string {
  return formatProviderName(item.practitioners[0]?.name?.[0])
}

type UndocumentedRowProps = {
  item: CareCoordinationEncounterItem
  isClosed: boolean
  onClose: (item: CareCoordinationEncounterItem) => void
  onOpen: (item: CareCoordinationEncounterItem) => void
}

function UndocumentedRow({ item, isClosed, onClose, onOpen }: UndocumentedRowProps) {
  const navigate = useNavigate()
  const start = item.resource.period?.start ?? ''
  const practitionerName = getPractitionerLabel(item)
  const overdue = start ? isOverdue(start) : false
  const accentColor = overdue ? 'bg-red-500' : 'bg-amber-500'
  const ageColor = overdue ? 'text-red-600' : 'text-amber-600'
  const buttonClass = overdue ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'

  return (
    <div
      className={`relative flex items-center justify-between border-b border-gray-100 px-4 py-3 last:border-b-0 ${
        isClosed ? 'opacity-60' : ''
      }`}
    >
      <span className={`absolute top-0 bottom-0 left-0 w-[3px] ${accentColor}`} />
      <div className="flex flex-1 items-start gap-3 pl-2">
        <IconUser size={16} className="mt-1 text-violet-500" />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold text-gray-900">{getPatientLabel(item)}</span>
          {practitionerName && <span className="text-xs text-gray-500">{practitionerName}</span>}
          {start && (
            <span className="text-xs text-gray-500">
              {formatStartLine(start, getDurationMinutes(item))}
            </span>
          )}
          {start && (
            <span className={`mt-1 text-xs font-semibold ${ageColor}`}>{formatTimeAgo(start)}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => item.resource.id && navigate(`/call/${item.resource.id}`)}
          className={`flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-[3px] !text-[11px] !font-semibold text-white transition-colors ${buttonClass}`}
        >
          Start SOAP <span aria-hidden="true">→</span>
        </button>
        {isClosed ? (
          <button
            type="button"
            onClick={() => onOpen(item)}
            title="Re-open this encounter"
            className="flex cursor-pointer items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
          >
            Open
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onClose(item)}
            title="Dismiss this encounter"
            className="flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
          >
            <IconX size={12} />
            Close
          </button>
        )}
      </div>
    </div>
  )
}

type SubmittedRowProps = {
  item: CareCoordinationEncounterItem
  isClosed: boolean
  isSelected: boolean
  onClose: (item: CareCoordinationEncounterItem) => void
  onOpen: (item: CareCoordinationEncounterItem) => void
  onSelectToggle: (item: CareCoordinationEncounterItem) => void
}

function SubmittedRow({
  item,
  isClosed,
  isSelected,
  onClose,
  onOpen,
  onSelectToggle,
}: SubmittedRowProps) {
  const navigate = useNavigate()
  const start = item.resource.period?.start
  const visitType = getEncounterVisitType(item)
  const practitionerName = getPractitionerLabel(item)
  const patientId = item.patient?.id

  return (
    <div
      onClick={() =>
        patientId &&
        navigate(buildPatientChartPath(patientId, 'progress-notes', item.documentReferenceId))
      }
      className={`relative flex cursor-pointer items-start justify-between border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-gray-50 ${
        isClosed ? 'opacity-60' : ''
      }`}
    >
      <span className="absolute top-0 bottom-0 left-0 w-[3px] bg-emerald-500" />
      <div className="flex flex-1 items-start gap-3 pl-2">
        {!isClosed && (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="xs"
              checked={isSelected}
              onChange={() => onSelectToggle(item)}
              styles={{
                input: {
                  cursor: 'pointer',
                  backgroundColor: isSelected ? 'rgb(229, 83, 61)' : undefined,
                  borderColor: isSelected ? 'rgb(229, 83, 61)' : undefined,
                },
              }}
              aria-label="Select encounter"
            />
          </div>
        )}
        <IconUser size={16} className="mt-1 text-violet-500" />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold text-gray-900">{getPatientLabel(item)}</span>
          {practitionerName && <span className="text-xs text-gray-500">{practitionerName}</span>}
          {start && <span className="text-xs text-gray-500">{formatSubmittedTime(start)}</span>}
          {visitType && <span className="text-xs text-gray-500">{visitType}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-emerald-600">Submitted</span>
        {isClosed ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onOpen(item)
            }}
            title="Re-open this encounter"
            className="flex cursor-pointer items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
          >
            Open
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onClose(item)
            }}
            title="Dismiss this encounter"
            className="flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
          >
            <IconX size={12} />
            Close
          </button>
        )}
      </div>
    </div>
  )
}

type EncountersProps = {
  practitionerIds?: string[]
}

export const Encounters = ({ practitionerIds }: EncountersProps) => {
  const middleware = useMiddlewareClient()
  const profile = useMedplumProfile()
  const currentPractitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined
  // Expand/collapse and the section filters are kept for the session so leaving
  // Home and coming back reopens both sections as the user left them (PD-1206).
  const [undocumentedOpen, setUndocumentedOpen] = useSessionState(
    'home:encounters:undocumentedOpen',
    true,
  )
  const [submittedOpen, setSubmittedOpen] = useSessionState('home:encounters:submittedOpen', false)
  // Shared by the Undocumented and Submitted rows, whose other chips are already
  // session-kept — see the same reasoning in Results.tsx (PD-1206).
  const [selectedPatient, setSelectedPatient] = useSessionState<Patient | null>(
    'home:encounters:selectedPatient',
    null,
  )
  // Remount counter for PatientSelect, not user state — deliberately not kept.
  const [patientSelectKey, setPatientSelectKey] = useState(0)
  const [undocumentedShowClosed, setUndocumentedShowClosed] = useSessionState(
    'home:encounters:undocumentedShowClosed',
    false,
  )
  const [submittedShowClosed, setSubmittedShowClosed] = useSessionState(
    'home:encounters:submittedShowClosed',
    false,
  )
  const [submittedSelected, setSubmittedSelected] = useState<Set<string>>(new Set())
  const patientId = selectedPatient?.id

  // Date range state (`YYYY-MM-DD` strings or null) — the single source of
  // truth for filtering each list. Pills are quick presets that update these;
  // the picker writes them directly. Both sections default to the `Today`
  // preset (`dateFrom = today, dateTo = today`).
  const today = dayjs().format(DATE_FORMAT)
  const [storedUndocumentedFrom, setUndocumentedDateFrom] = useSessionState<string | null>(
    'home:encounters:undocumentedDateFrom',
    today,
  )
  const [storedUndocumentedTo, setUndocumentedDateTo] = useSessionState<string | null>(
    'home:encounters:undocumentedDateTo',
    today,
  )
  const [storedSubmittedFrom, setSubmittedDateFrom] = useSessionState<string | null>(
    'home:encounters:submittedDateFrom',
    today,
  )
  const [storedSubmittedTo, setSubmittedDateTo] = useSessionState<string | null>(
    'home:encounters:submittedDateTo',
    today,
  )
  // Preset identity is stored separately from the absolute picker values. That
  // lets an explicit preset follow today across midnight without reverse-matching
  // a hand-picked historical range that happens to have the same shape (PD-1206).
  const [undocumentedPreset, setUndocumentedPreset] = useSessionState<UndocumentedFilterId | null>(
    'home:encounters:undocumentedPreset',
    'today',
  )
  const [submittedPreset, setSubmittedPreset] = useSessionState<SubmittedFilterId | null>(
    'home:encounters:submittedPreset',
    'today',
  )
  const { dateFrom: undocumentedDateFrom, dateTo: undocumentedDateTo } = resolvePresetRange(
    undocumentedPreset,
    storedUndocumentedFrom,
    storedUndocumentedTo,
  )
  const { dateFrom: submittedDateFrom, dateTo: submittedDateTo } = resolvePresetRange(
    submittedPreset,
    storedSubmittedFrom,
    storedSubmittedTo,
  )

  const [undocumented, setUndocumented] = useState<CareCoordinationEncounterItem[]>([])
  const [submittedList, setSubmittedList] = useState<CareCoordinationEncounterItem[]>([])
  const [undocumentedLoading, setUndocumentedLoading] = useState(false)
  const [submittedLoading, setSubmittedLoading] = useState(false)
  const [refreshVersion, setRefreshVersion] = useState(0)

  const undocumentedActivePreset = undocumentedPreset
  const submittedActivePreset = submittedPreset

  useEffect(() => {
    if (!practitionerIds?.length) {
      setUndocumented([])
      return
    }

    let cancelled = false
    setUndocumentedLoading(true)
    const dateFrom = undocumentedDateFrom
      ? dayjs(undocumentedDateFrom).startOf('day').toISOString()
      : undefined
    const dateTo = undocumentedDateTo
      ? dayjs(undocumentedDateTo).endOf('day').toISOString()
      : undefined

    fetchCareCoordinationEncounters(middleware, {
      practitionerIds,
      progressNoteStatus: ['not-submitted'],
      excludeWithStatus: ['noshow'],
      count: FETCH_PAGE_SIZE,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(patientId ? { patientId } : {}),
      ...(currentPractitionerId && !undocumentedShowClosed
        ? { excludeClosedBy: currentPractitionerId }
        : {}),
    })
      .then((response) => {
        if (cancelled) return
        setUndocumented(response.encounters)
      })
      .catch((err) => {
        if (cancelled) return
        logError('home', 'Failed to fetch undocumented encounters', err)
      })
      .finally(() => {
        if (!cancelled) setUndocumentedLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    middleware,
    practitionerIds,
    patientId,
    currentPractitionerId,
    undocumentedShowClosed,
    undocumentedDateFrom,
    undocumentedDateTo,
    refreshVersion,
  ])

  useEffect(() => {
    if (!practitionerIds?.length) {
      setSubmittedList([])
      return
    }

    let cancelled = false
    setSubmittedLoading(true)
    const dateFrom = submittedDateFrom
      ? dayjs(submittedDateFrom).startOf('day').toISOString()
      : undefined
    const dateTo = submittedDateTo ? dayjs(submittedDateTo).endOf('day').toISOString() : undefined

    fetchCareCoordinationEncounters(middleware, {
      practitionerIds,
      progressNoteStatus: ['submitted'],
      count: FETCH_PAGE_SIZE,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(patientId ? { patientId } : {}),
      ...(currentPractitionerId && !submittedShowClosed
        ? { excludeClosedBy: currentPractitionerId }
        : {}),
    })
      .then((response) => {
        if (cancelled) return
        setSubmittedList(response.encounters)
      })
      .catch((err) => {
        if (cancelled) return
        logError('home', 'Failed to fetch submitted encounters', err)
      })
      .finally(() => {
        if (!cancelled) setSubmittedLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    middleware,
    practitionerIds,
    patientId,
    currentPractitionerId,
    submittedShowClosed,
    submittedDateFrom,
    submittedDateTo,
    refreshVersion,
  ])

  const applyUndocumentedPreset = (presetId: UndocumentedFilterId) => {
    setUndocumentedPreset(presetId)
    setUndocumentedDateFrom(presetIdToDateFrom(presetId))
    setUndocumentedDateTo(dayjs().format(DATE_FORMAT))
  }

  const handleUndocumentedDateFromChange = (value: string | null) => {
    setUndocumentedPreset(null)
    setUndocumentedDateFrom(value)
    setUndocumentedDateTo(undocumentedDateTo)
  }

  const handleUndocumentedDateToChange = (value: string | null) => {
    setUndocumentedPreset(null)
    setUndocumentedDateFrom(undocumentedDateFrom)
    setUndocumentedDateTo(value)
  }

  const refreshEncounterLists = () => setRefreshVersion((v) => v + 1)

  const handleClose = async (item: CareCoordinationEncounterItem) => {
    const encounterId = item.resource.id
    if (!encounterId || !currentPractitionerId) return

    const newTag = {
      system: ENCOUNTER_CLOSED_BY_TAG_SYSTEM,
      code: currentPractitionerId,
    }

    const addTagInPlace = (i: CareCoordinationEncounterItem) =>
      i.resource.id === encounterId
        ? {
            ...i,
            resource: {
              ...i.resource,
              meta: {
                ...i.resource.meta,
                tag: [...(i.resource.meta?.tag ?? []), newTag],
              },
            },
          }
        : i

    // Per-section behavior: if "Show closed" is on, mark in place; otherwise remove.
    setUndocumented((prev) =>
      undocumentedShowClosed
        ? prev.map(addTagInPlace)
        : prev.filter((i) => i.resource.id !== encounterId),
    )
    setSubmittedList((prev) =>
      submittedShowClosed
        ? prev.map(addTagInPlace)
        : prev.filter((i) => i.resource.id !== encounterId),
    )

    try {
      await closeEncounter(middleware, encounterId)
    } catch (err) {
      logError('home', 'Failed to close encounter', err)
      showNotification({
        color: 'red',
        title: 'Unable to dismiss encounter',
        message: normalizeErrorString(err),
      })
      refreshEncounterLists()
    }
  }

  const toggleSelected = (
    setSelected: Dispatch<SetStateAction<Set<string>>>,
    item: CareCoordinationEncounterItem,
  ) => {
    const id = item.resource.id
    if (!id) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkClose = async (
    selected: Set<string>,
    setSelected: Dispatch<SetStateAction<Set<string>>>,
    sectionShowsClosed: boolean,
    setList: Dispatch<SetStateAction<CareCoordinationEncounterItem[]>>,
  ) => {
    if (!currentPractitionerId || selected.size === 0) return

    const ids = Array.from(selected)
    const newTag = { system: ENCOUNTER_CLOSED_BY_TAG_SYSTEM, code: currentPractitionerId }

    const addTagInPlace = (i: CareCoordinationEncounterItem) =>
      ids.includes(i.resource.id ?? '')
        ? {
            ...i,
            resource: {
              ...i.resource,
              meta: {
                ...i.resource.meta,
                tag: [...(i.resource.meta?.tag ?? []), newTag],
              },
            },
          }
        : i

    setList((prev) =>
      sectionShowsClosed
        ? prev.map(addTagInPlace)
        : prev.filter((i) => !ids.includes(i.resource.id ?? '')),
    )

    const results = await Promise.allSettled(ids.map((id) => closeEncounter(middleware, id)))
    const failures = results.filter((r) => r.status === 'rejected')
    if (failures.length > 0) {
      const failedIds = results.flatMap((result, index) =>
        result.status === 'rejected' ? [ids[index]] : [],
      )
      logError('home', 'Some bulk closes failed', failures)
      showNotification({
        color: 'red',
        title: 'Some encounters could not be dismissed',
        message: `${failures.length} of ${ids.length} failed. Refresh to see current state.`,
      })
      setSelected(new Set(failedIds))
      refreshEncounterLists()
      return
    }
    setSelected(new Set())
  }

  const handleOpen = async (item: CareCoordinationEncounterItem) => {
    const encounterId = item.resource.id
    if (!encounterId || !currentPractitionerId) return

    const removeTagInPlace = (i: CareCoordinationEncounterItem) =>
      i.resource.id === encounterId
        ? {
            ...i,
            resource: {
              ...i.resource,
              meta: {
                ...i.resource.meta,
                tag: (i.resource.meta?.tag ?? []).filter(
                  (t) =>
                    !(
                      t.system === ENCOUNTER_CLOSED_BY_TAG_SYSTEM &&
                      t.code === currentPractitionerId
                    ),
                ),
              },
            },
          }
        : i

    setUndocumented((prev) => prev.map(removeTagInPlace))
    setSubmittedList((prev) => prev.map(removeTagInPlace))

    try {
      await uncloseEncounter(middleware, encounterId)
    } catch (err) {
      logError('home', 'Failed to re-open encounter', err)
      showNotification({
        color: 'red',
        title: 'Unable to re-open encounter',
        message: normalizeErrorString(err),
      })
      refreshEncounterLists()
    }
  }

  const clearUndocumentedFilters = () => {
    setUndocumentedPreset(null)
    setUndocumentedDateFrom(null)
    setUndocumentedDateTo(null)
    setSelectedPatient(null)
    setPatientSelectKey((k) => k + 1)
  }

  const applySubmittedPreset = (presetId: SubmittedFilterId) => {
    setSubmittedPreset(presetId)
    setSubmittedDateFrom(presetIdToDateFrom(presetId))
    setSubmittedDateTo(dayjs().format(DATE_FORMAT))
  }

  const handleSubmittedDateFromChange = (value: string | null) => {
    setSubmittedPreset(null)
    setSubmittedDateFrom(value)
    setSubmittedDateTo(submittedDateTo)
  }

  const handleSubmittedDateToChange = (value: string | null) => {
    setSubmittedPreset(null)
    setSubmittedDateFrom(submittedDateFrom)
    setSubmittedDateTo(value)
  }

  const clearSubmittedFilters = () => {
    setSubmittedPreset(null)
    setSubmittedDateFrom(null)
    setSubmittedDateTo(null)
    setSelectedPatient(null)
    setPatientSelectKey((k) => k + 1)
  }

  const hasUndocumentedFilters =
    undocumentedDateFrom !== null || undocumentedDateTo !== null || Boolean(selectedPatient)
  const hasSubmittedFilters =
    submittedDateFrom !== null || submittedDateTo !== null || Boolean(selectedPatient)

  const undocumentedSorted = useMemo(() => {
    const now = dayjs()
    return [...undocumented]
      .filter((item) => {
        const start = item.resource.period?.start
        return start ? !dayjs(start).isAfter(now) : true
      })
      .sort((a, b) => {
        const aStart = a.resource.period?.start ?? ''
        const bStart = b.resource.period?.start ?? ''
        return aStart < bStart ? 1 : aStart > bStart ? -1 : 0
      })
  }, [undocumented])

  const submittedSorted = useMemo(
    () =>
      [...submittedList].sort((a, b) => {
        const aStart = a.resource.period?.start ?? ''
        const bStart = b.resource.period?.start ?? ''
        return aStart < bStart ? 1 : aStart > bStart ? -1 : 0
      }),
    [submittedList],
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="shrink-0">
        <PatientSelect
          key={patientSelectKey}
          setSelectedPatient={setSelectedPatient}
          initialPatient={selectedPatient}
          placeholder="Filter by patient (optional)"
          hideAddPatientButton
        />
      </div>
      <CollapsibleSectionCard
        icon={<IconStethoscope size={20} className="text-amber-600" />}
        title="Undocumented"
        count={undocumentedSorted.length}
        countClassName="text-amber-600"
        open={undocumentedOpen}
        onToggle={() => setUndocumentedOpen((v) => !v)}
      >
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-4 pt-2 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterPills
              options={ENCOUNTER_FILTERS}
              value={undocumentedActivePreset}
              onChange={applyUndocumentedPreset}
            />
            {hasUndocumentedFilters && (
              <button
                type="button"
                onClick={clearUndocumentedFilters}
                className="cursor-pointer rounded-full border border-gray-300 px-2 !py-[1px] !text-[12px] font-normal text-gray-700 transition-colors hover:bg-gray-50"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Checkbox
              size="xs"
              label="Show dismissed"
              checked={undocumentedShowClosed}
              onChange={(e) => setUndocumentedShowClosed(e.currentTarget.checked)}
              styles={{
                label: { fontSize: 12, cursor: 'pointer' },
                input: {
                  cursor: 'pointer',
                  backgroundColor: undocumentedShowClosed ? 'rgb(229, 83, 61)' : undefined,
                  borderColor: undocumentedShowClosed ? 'rgb(229, 83, 61)' : undefined,
                },
              }}
            />
            <DateRangeRow
              dateFrom={undocumentedDateFrom}
              dateTo={undocumentedDateTo}
              onDateFromChange={handleUndocumentedDateFromChange}
              onDateToChange={handleUndocumentedDateToChange}
            />
          </div>
        </div>
        <div className="relative min-h-0 flex-1 overflow-y-auto">
          <LoadingOverlay
            visible={undocumentedLoading}
            zIndex={10}
            overlayProps={{ blur: 1 }}
            loaderProps={{ size: 'sm', color: 'blue' }}
          />
          {undocumentedSorted.length === 0 && !undocumentedLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-4 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50">
                <IconSearch size={20} className="text-orange-500" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-gray-900">
                  No results found for this date range
                </p>
                <p className="max-w-sm text-xs text-gray-500">
                  Results may be hidden by the active filters. Try expanding the date range or
                  clearing all filters.
                </p>
              </div>
              <button
                type="button"
                onClick={clearUndocumentedFilters}
                className="mt-1 cursor-pointer rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            undocumentedSorted.map((item) => (
              <UndocumentedRow
                key={item.resource.id}
                item={item}
                isClosed={
                  currentPractitionerId ? isEncounterClosedBy(item, currentPractitionerId) : false
                }
                onClose={handleClose}
                onOpen={handleOpen}
              />
            ))
          )}
        </div>
      </CollapsibleSectionCard>

      <CollapsibleSectionCard
        icon={<IconCircleCheckFilled size={20} className="text-emerald-600" />}
        title="Submitted"
        count={submittedSorted.length}
        countClassName="text-emerald-600"
        open={submittedOpen}
        onToggle={() => setSubmittedOpen((v) => !v)}
      >
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-4 pt-2 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterPills
              options={ENCOUNTER_FILTERS}
              value={submittedActivePreset}
              onChange={applySubmittedPreset}
            />
            {hasSubmittedFilters && (
              <button
                type="button"
                onClick={clearSubmittedFilters}
                className="cursor-pointer rounded-full border border-gray-300 px-2 !py-[1px] !text-[12px] font-normal text-gray-700 transition-colors hover:bg-gray-50"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Checkbox
              size="xs"
              label="Show dismissed"
              checked={submittedShowClosed}
              onChange={(e) => setSubmittedShowClosed(e.currentTarget.checked)}
              styles={{
                label: { fontSize: 12, cursor: 'pointer' },
                input: {
                  cursor: 'pointer',
                  backgroundColor: submittedShowClosed ? 'rgb(229, 83, 61)' : undefined,
                  borderColor: submittedShowClosed ? 'rgb(229, 83, 61)' : undefined,
                },
              }}
            />
            <DateRangeRow
              dateFrom={submittedDateFrom}
              dateTo={submittedDateTo}
              onDateFromChange={handleSubmittedDateFromChange}
              onDateToChange={handleSubmittedDateToChange}
            />
          </div>
        </div>
        {(() => {
          const openIds = submittedSorted
            .filter(
              (i) => !(currentPractitionerId && isEncounterClosedBy(i, currentPractitionerId)),
            )
            .map((i) => i.resource.id ?? '')
            .filter(Boolean)
          const selectedCount = openIds.filter((id) => submittedSelected.has(id)).length
          const allSelected = openIds.length > 0 && selectedCount === openIds.length
          const someSelected = selectedCount > 0 && !allSelected
          if (openIds.length === 0) return null
          return (
            <div className="flex shrink-0 items-center justify-between gap-3 border-y border-gray-100 bg-gray-50 px-4 py-2">
              <Checkbox
                size="xs"
                label="Select all open items"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) =>
                  setSubmittedSelected(e.currentTarget.checked ? new Set(openIds) : new Set())
                }
                styles={{
                  label: { fontSize: 12, cursor: 'pointer' },
                  input: {
                    cursor: 'pointer',
                    backgroundColor: allSelected || someSelected ? 'rgb(229, 83, 61)' : undefined,
                    borderColor: allSelected || someSelected ? 'rgb(229, 83, 61)' : undefined,
                  },
                }}
              />
              {selectedCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-600">{selectedCount} selected</span>
                  <button
                    type="button"
                    onClick={() => setSubmittedSelected(new Set())}
                    className="cursor-pointer rounded-md border border-gray-300 px-2 py-[2px] text-[10px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Clear selection
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleBulkClose(
                        submittedSelected,
                        setSubmittedSelected,
                        submittedShowClosed,
                        setSubmittedList,
                      )
                    }
                    className="cursor-pointer rounded-md px-2 py-[2px] text-[10px] font-semibold text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: 'rgb(229, 83, 61)' }}
                  >
                    Dismiss selected
                  </button>
                </div>
              )}
            </div>
          )
        })()}
        <div className="relative min-h-0 flex-1 overflow-y-auto">
          <LoadingOverlay
            visible={submittedLoading}
            zIndex={10}
            overlayProps={{ blur: 1 }}
            loaderProps={{ size: 'sm', color: 'blue' }}
          />
          {submittedSorted.length === 0 && !submittedLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-4 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50">
                <IconSearch size={20} className="text-orange-500" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-gray-900">
                  No results found for this date range
                </p>
                <p className="max-w-sm text-xs text-gray-500">
                  Results may be hidden by the active filters. Try expanding the date range or
                  clearing all filters.
                </p>
              </div>
              <button
                type="button"
                onClick={clearSubmittedFilters}
                className="mt-1 cursor-pointer rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            submittedSorted.map((item) => (
              <SubmittedRow
                key={item.resource.id}
                item={item}
                isClosed={
                  currentPractitionerId ? isEncounterClosedBy(item, currentPractitionerId) : false
                }
                isSelected={submittedSelected.has(item.resource.id ?? '')}
                onClose={handleClose}
                onOpen={handleOpen}
                onSelectToggle={(it) => toggleSelected(setSubmittedSelected, it)}
              />
            ))
          )}
        </div>
      </CollapsibleSectionCard>
    </div>
  )
}
